import { ChildProps } from '@apollo/client/react/hoc';
import { gql } from '@apollo/client';
import React from 'react';
import classNames from 'classnames';
import bind from '../bind';
import { withUserContext, IUserContext } from './user_context_provider';
import CmsPageComponent from './cms_page_component';
import DynamicPageHead from './dynamic_page_head';
import CmsPageMetadata from './cms_page_metadata';
import { CommonsCmsCmsEmbed } from '../rql';
import { cmsExperimentComponentFilter, trackCmsExperimentQualifyingEvent } from '../track_cms_experiment_component_filter';
import { withGraphql } from '../with_graphql';
import { withRouter, WithRouterProps } from 'react-router';

/**
 *  off: Don't fetch CMS page+components on the server, initial render is empty
 *  static-only: Fetch CMS page and component configuration server-side, but nothing else.
 *               Renders component placeholders, and any rows that don't rely on additional queries
 *  full: Fetch data server-side for the first three components
 */
type SSRMode = 'off' | 'static-only' | 'full';

interface IOwnProps {
  slug: string;
  ssrMode: SSRMode;
  renderHeader?: boolean;
}

type IProps = ChildProps<IUserContext & IOwnProps & WithRouterProps, CommonsCmsCmsEmbed.Query> ;

function CmsHeader({ page }) {
  return (
    <>
      <CmsPageMetadata page={page} />
      <DynamicPageHead
        actionText={page.actionText}
        actionUrl={page.actionUrl}
        authorName={page.authorName}
        goLiveAt={page.goLiveAt}
        headerStyle={page.headerStyle}
        imageUrl={page.image?.imageUrl}
        pageType={page.pageType}
        searchPlaceholder={page.searchPlaceholder}
        subtitle={page.description}
        title={page.title}
        secondaryImageUrl={page.secondaryImage?.imageUrl}
      />
    </>
  );
}

export class CmsEmbed extends React.Component<IProps, null> {
  componentDidMount() {
    if (!this.page || this.page?.pageNotFound) return;

    trackCmsExperimentQualifyingEvent({
      components: this.page?.components || [],
      user: this.props.user,
    });
  }

  componentDidUpdate(oldProps) {
    if (!this.page?.pageNotFound && this.page?.slug !== oldProps.data?.page?.slug) {
      trackCmsExperimentQualifyingEvent({
        components: this.page?.components || [],
        user: this.props.user,
      });
    }
  }

  get page() {
    return this.props.data?.pages?.[0];
  }

  @bind
  filterComponentsForExperiment(component: CommonsCmsCmsEmbed.Components) {
    return cmsExperimentComponentFilter({
      user: this.props.user,
      component,
    });
  }

  getComponentsForEmbed() {
    if (!this.page.components) { return []; }

    return this.page.components.filter((component) => {
      return component.layoutSection === 'main';
    });
  }

  renderComponents(components: CommonsCmsCmsEmbed.Components[]) {
    return components.filter(this.filterComponentsForExperiment).map((component, index) => {
      return (
        <CmsPageComponent
          key={component.id}
          component={component}
          canSsr={this.props.ssrMode === 'full'}
          layoutSlug={this.page.layoutSlug}
          position={index}
        />
      );
    });
  }

  renderEmbed() {
    const components = this.getComponentsForEmbed();

    return (
      <div className="dynamic-page__one-col">
        {this.props.renderHeader && <CmsHeader page={this.page} />}
        {this.renderComponents(components)}
      </div>
    );
  }

  render() {
    const { page } = this;
    if (!page || page.pageNotFound) { return null; }

    const classes = classNames(
      'dynamic-page',
      { 'dynamic-page--flush-with-header': 'none' },
    );

    return (
      <div className={classes}>
        <div>
          {this.renderEmbed()}
        </div>
      </div>
    );
  }
}

export const CMS_EMBED_QUERY = gql`
query Commons_Cms_CmsEmbed(
  $slug: String
  $admin: Boolean
)
{
  pages: cmsPagesFetch(input: {
    slugs: [$slug],
    includeDrafts: $admin
  }) {
    _id
    id
    slug
    actionText
    actionUrl
    authorName
    description
    goLiveAt
    headerStyle
    layoutSlug
    pageType
    searchPlaceholder
    title
    metaTitle
    metaDescription
    noIndex
    image {
      imageUrl
    }
    metaImage {
      imageUrl
    }
    secondaryImage {
      imageUrl
    }
    components {
      _id
      id
      position
      componentType
      data
      layoutSection
      experimentKey
      experimentBucket
    }
    live
    pageNotFound
  }
}`;

const connect = withGraphql<IProps, CommonsCmsCmsEmbed.Query>(
  CMS_EMBED_QUERY,
  {
    options: (ownProps) => {
      return {
        ssr: ownProps.ssrMode !== 'off',
        variables: {
          slug: ownProps.slug,
          admin: ownProps.user?.isAdmin,
        },
      };
    },
  },
);

export default withUserContext<IOwnProps>(withRouter(connect(CmsEmbed)));
